import { ReactNode } from "react";
import { createGlobalStyle, ThemeProvider } from "styled-components/macro";
import colors from "./colors";
import sizes from "./sizes.json";
import fonts from "./fonts.json";
import icons from "./icons.json";
import radius from "./radius.json";
import breakpoints from "./breakpoints.json";
import containers from "./containers.json";
import zindexes from "./zindexes.json";
import cssVars from "./cssVars";
import { boxshadows } from "./boxshadows";
import { visiblescroll } from "./visiblescroll";

import "@chanzuckerberg/eds/index.css";
import "@chanzuckerberg/eds/fonts.css";
import "./app-theme.css";

export const theme = {
  ...colors,
  ...sizes,
  ...fonts,
  ...icons,
  ...breakpoints,
  ...containers,
  ...zindexes,
  ...radius,
  ...boxshadows,
  ...visiblescroll,
  cssVars,
};

export type Sizes = typeof sizes;

export type Color = typeof colors;

const GlobalStyles = createGlobalStyle`
  /* BEGIN EDS TOKEN SETUP */
  :root {
    /**
     * Define any along-specific tokens that aren't defined in the existing theme files.
     */
    --along-preset-secondary-001: var(--eds-font-weight-medium) var(--eds-font-size-40)/1.2 var(--eds-font-family-secondary);
    --along-preset-secondary-001-light: var(--eds-font-weight-light) var(--eds-font-size-40)/1.2 var(--eds-font-family-secondary);
    --along-preset-secondary-002: var(--eds-font-weight-medium) var(--eds-font-size-32)/1.25 var(--eds-font-family-secondary);
    --along-preset-secondary-002-light: var(--eds-font-weight-light) var(--eds-font-size-32)/1.25 var(--eds-font-family-secondary);
    --along-preset-secondary-003: var(--eds-font-weight-medium) var(--eds-font-size-28)/1.42857143 var(--eds-font-family-secondary);
    --along-preset-secondary-003-light: var(--eds-font-weight-light) var(--eds-font-size-28)/1.42857143 var(--eds-font-family-secondary);
    --along-preset-secondary-004: var(--eds-font-weight-medium) 1.625rem/1.38461538 var(--eds-font-family-secondary);
    --along-preset-secondary-004-light: var(--eds-font-weight-light) 1.625rem/1.38461538 var(--eds-font-family-secondary);
    --along-preset-secondary-005: var(--eds-font-weight-medium) var(--eds-font-size-24)/1.33333333 var(--eds-font-family-secondary);
    --along-preset-secondary-005-light: var(--eds-font-weight-light) var(--eds-font-size-24)/1.33333333 var(--eds-font-family-secondary);
    --along-preset-secondary-006: var(--eds-font-weight-medium) 1.375rem/1.36363636 var(--eds-font-family-secondary);
    --along-preset-secondary-006-light: var(--eds-font-weight-light) 1.375rem/1.36363636 var(--eds-font-family-secondary);
    --along-preset-secondary-007: var(--eds-font-weight-medium) var(--eds-font-size-18)/1.75 var(--eds-font-family-secondary);
    --along-preset-secondary-007-light: var(--eds-font-weight-light) var(--eds-font-size-18)/1.75 var(--eds-font-family-secondary);
    --along-preset-secondary-008: var(--eds-font-weight-medium) var(--eds-font-size-16)/1.5 var(--eds-font-family-secondary);
    --along-preset-secondary-008-light: var(--eds-font-weight-light) var(--eds-font-size-16)/1.5 var(--eds-font-family-secondary);

    --along-preset-mobile-001-light: var(--eds-font-weight-light) var(--eds-font-size-32)/1.25 var(--eds-font-family-primary);
    --along-preset-secondary-mobile-001-light: var(--eds-font-weight-light) var(--eds-font-size-32)/1.25 var(--eds-font-family-secondary);
    --along-preset-mobile-002-light: var(--eds-font-weight-light) var(--eds-font-size-28)/1.28571429 var(--eds-font-family-primary);
    --along-preset-secondary-mobile-002-light: var(--eds-font-weight-light) var(--eds-font-size-28)/1.28571429 var(--eds-font-family-secondary);
    --along-preset-mobile-003-light: var(--eds-font-weight-light) var(--eds-font-size-24)/1.33333333 var(--eds-font-family-primary);
    --along-preset-secondary-mobile-003-light: var(--eds-font-weight-light) var(--eds-font-size-24)/1.33333333 var(--eds-font-family-secondary);

    /* Convert theme colors into similarly-named CSS variables */
    --along-color-ColorNeutralWhite: ${theme.ColorNeutralWhite};
    --along-color-ColorNeutral100: ${theme.ColorNeutral100};
    --along-color-ColorNeutral200: ${theme.ColorNeutral200};
    --along-color-ColorNeutral300: ${theme.ColorNeutral300};
    --along-color-ColorNeutral400: ${theme.ColorNeutral400};
    --along-color-ColorNeutral500: ${theme.ColorNeutral500};
    --along-color-ColorNeutral600: ${theme.ColorNeutral600};
    --along-color-ColorNeutral700: ${theme.ColorNeutral700};
    --along-color-ColorNeutralBlack: ${theme.ColorNeutralBlack};
    --along-color-ColorWhite: ${theme.ColorWhite};
    --along-color-ColorGray100: ${theme.ColorGray100};
    --along-color-ColorGray300: ${theme.ColorGray300};
    --along-color-ColorGray500: ${theme.ColorGray500};
    --along-color-ColorBlack: ${theme.ColorBlack};
    --along-color-ColorBlurple: ${theme.ColorBlurple};
    --along-color-ColorBlurple50: ${theme.ColorBlurple50};
    --along-color-ColorBlurple100: ${theme.ColorBlurple100};
    --along-color-ColorBlurple200: ${theme.ColorBlurple200};
    --along-color-ColorBlurple300: ${theme.ColorBlurple300};
    --along-color-ColorBlurple400: ${theme.ColorBlurple400};
    --along-color-ColorBlurple500: ${theme.ColorBlurple500};
    --along-color-ColorSpace25: ${theme.ColorSpace25};
    --along-color-ColorSpace50: ${theme.ColorSpace50};
    --along-color-ColorSpace75: ${theme.ColorSpace75};
    --along-color-ColorSpace100: ${theme.ColorSpace100};
    --along-color-ColorSpace200: ${theme.ColorSpace200};
    --along-color-ColorSpace300: ${theme.ColorSpace300};
    --along-color-ColorSpace400: ${theme.ColorSpace400};
    --along-color-ColorSpace500: ${theme.ColorSpace500};
    --along-color-ColorMint100: ${theme.ColorMint100};
    --along-color-ColorMint200: ${theme.ColorMint200};
    --along-color-ColorMint300: ${theme.ColorMint300};
    --along-color-ColorMint400: ${theme.ColorMint400};
    --along-color-ColorMint500: ${theme.ColorMint500};
    --along-color-ColorSun100: ${theme.ColorSun100};
    --along-color-ColorSun200: ${theme.ColorSun200};
    --along-color-ColorSun300: ${theme.ColorSun300};
    --along-color-ColorSun400: ${theme.ColorSun400};
    --along-color-ColorSun500: ${theme.ColorSun500};
    --along-color-ColorCarrot100: ${theme.ColorCarrot100};
    --along-color-ColorCarrot200: ${theme.ColorCarrot200};
    --along-color-ColorCarrot300: ${theme.ColorCarrot300};
    --along-color-ColorCarrot400: ${theme.ColorCarrot400};
    --along-color-ColorCarrot500: ${theme.ColorCarrot500};
    --along-color-ColorGum100: ${theme.ColorGum100};
    --along-color-ColorGum400: ${theme.ColorGum400};
    --along-color-ColorGum500: ${theme.ColorGum500};
    --along-color-ColorPine: ${theme.ColorPine};
    --along-color-ColorPine500: ${theme.ColorPine500};
    --along-color-ColorPine400: ${theme.ColorPine400};
    --along-color-ColorPine300: ${theme.ColorPine300};
    --along-color-ColorPine200: ${theme.ColorPine200};
    --along-color-ColorPine100: ${theme.ColorPine100};
    --along-color-ColorLemon: ${theme.ColorLemon};
    --along-color-ColorLemon100: ${theme.ColorLemon100};
    --along-color-ColorLemon200: ${theme.ColorLemon200};
    --along-color-ColorLemon500: ${theme.ColorLemon500};
    --along-color-ColorSky: ${theme.ColorSky};
    --along-color-ColorSky100: ${theme.ColorSky100};
    --along-color-ColorSky200: ${theme.ColorSky200};
    --along-color-ColorSky500: ${theme.ColorSky500};
    --along-color-ColorEraserRed100: ${theme.ColorEraserRed100};
    --along-color-ColorEraserRed200: ${theme.ColorEraserRed200};
    --along-color-ColorEraserRed300: ${theme.ColorEraserRed300};
    --along-color-ColorEraserRed400: ${theme.ColorEraserRed400};

  }

  /**
   * Custom, tailwind-like overrides for anything not possible via EDS tokens in current version
   * Note: as a temporary measure, consider using !important only as a last resort, but file a bug
   *       with the EDS team with any such exceptions!
   */

  /* END EDS TOKEN SETUP */

  html, body {
    height: 100%;
  }

  body {
    color: ${theme.ColorSpace500};
    font-family: ${theme.FontFamilyBodySansSerif};
  }

  /**
   * Hide focus outlines from mouse interaction, but still use them for keyboard interaction.
   * This relies on the https://github.com/WICG/focus-visible polyfill.
   */
  .js-focus-visible :focus:not(.focus-visible) {
    outline: none;
  }

  .modal-backdrop {
    /**
     * Override the Bootstrap Modal background fade color to match the product design.
     */
    background-color: ${theme.ColorSpace500};
  }
`;

export default function ConnectionsThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      {children}
    </ThemeProvider>
  );
}
